import { useEffect, useState } from 'react';

import { authSlice } from './auth.slice';
import { authService } from './authService';
import { useAppDispatch } from 'src/app/store/hooks';
import { useProfileRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';

export const MAX_MEMBERSHIP_REFETCHES = 2;
export const MEMBERSHIP_REFETCH_DELAY_MS = 1000;

export function useFetchAndEnsureProfile() {
  const dispatch = useAppDispatch();
  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useProfileRetrieveQuery();
  const [isVerifyingMemberships, setIsVerifyingMemberships] = useState(false);

  useEffect(() => {
    if (profile) {
      dispatch(authSlice.actions.signIn(profile));
    }
  }, [profile, dispatch]);

  useEffect(() => {
    // Only redirect when we genuinely have no profile. redirectUnauthedUser
    // re-auths via the Hosted UI, or signs out fully if a stale session would
    // otherwise loop. A failed *refetch* from the membership-verification loop
    // below sets `error` while the loaded `profile` is still present — that must
    // not bounce an already-authed user.
    if (error && !profile) {
      // Fire-and-forget: redirectUnauthedUser handles its own errors.
      void authService.redirectUnauthedUser();
    }
  }, [error, profile]);

  // A freshly-loaded profile can briefly have no memberships — a stale cached
  // snapshot, or a just-provisioned account whose membership isn't visible yet.
  // Refetch a bounded number of times before trusting the empty result, so
  // RequireAuth doesn't flash the no-organization notice on a transient one.
  useEffect(() => {
    if (!profile || profile.memberships.length > 0) {
      setIsVerifyingMemberships(false);
      return;
    }

    const controller = new AbortController();
    // Read via a call so TS doesn't narrow it to a constant between awaits.
    const isCancelled = () => controller.signal.aborted;
    setIsVerifyingMemberships(true);

    void (async () => {
      for (let attempt = 0; attempt < MAX_MEMBERSHIP_REFETCHES; attempt++) {
        await new Promise((resolve) =>
          setTimeout(resolve, MEMBERSHIP_REFETCH_DELAY_MS)
        );
        if (isCancelled()) return;

        const refreshed = await refetch()
          .unwrap()
          .catch(() => undefined);
        if (isCancelled()) return;
        if (refreshed && refreshed.memberships.length > 0) {
          setIsVerifyingMemberships(false);
          return;
        }
      }
      if (!isCancelled()) setIsVerifyingMemberships(false);
    })();

    return () => {
      controller.abort();
    };
  }, [profile, refetch]);

  return {
    isLoading: isLoading || isVerifyingMemberships,
  };
}
